import React, { ChangeEvent } from 'react';
import { Control, FieldValues, useController } from 'react-hook-form';

import { FieldError } from '@/components/UI/Fields/FieldError';
import { FieldProps } from '@/components/UI/Fields/types';
import { Input } from '@/components/UI/Input/Input';
import { Label } from '@/components/UI/Label/Label';
import { TextArea } from '@/components/UI/TextArea/TextArea';

import styles from '../Fields.module.css';

export type FieldComponentProps<T extends FieldValues> = FieldProps<T> &
  Omit<React.ComponentPropsWithoutRef<typeof TextArea | typeof Input>, keyof FieldProps<T>> & {
    control: Control<T>;
    rows?: number;
    narrowVariant?: boolean;
    displayBlock?: boolean;
    width?: string;
    type?: string;
    onChangeCallback?: (value: ChangeEvent<HTMLInputElement>) => void;
    showOptional?: boolean;
    geavanceerdZoekenVariant?: boolean;
    multiple?: boolean;
    accept?: string;
  };

function CreateFieldComponentWrapper(FieldComponent: React.ComponentType<any>) {
  function CreateFieldComponent<FV extends FieldValues>({
    control,
    name,
    type,
    label,
    width,
    displayBlock = true,
    required,
    defaultValue,
    rules,
    onChangeCallback,
    showOptional = true,
    ...rest
  }: FieldComponentProps<FV>) {
    const { field, fieldState } = useController({
      name,
      control,
      rules: { required: required ? 'verplicht' : undefined, ...rules },
      defaultValue,
    });

    return (
      <div style={{ display: displayBlock ? 'block' : 'flex' }}>
        <Label htmlFor={field.name}>
          <div style={{ marginTop: '12px', marginBottom: '1px', display: 'flex', alignItems: 'baseline', gap: '8p' }}>
            {label}
            {!required && showOptional && <span className={styles.optionalLabel}>Optioneel</span>}
          </div>
        </Label>
        <div style={{ maxWidth: width, display: 'flex', flexDirection: 'column' }}>
          <FieldComponent
            {...field}
            type={type}
            isInvalid={!!fieldState.error}
            id={field.name}
            onChange={(value) => {
              field.onChange(value);
              onChangeCallback?.(value);
            }}
            {...rest}
          />
          <FieldError width='auto' error={fieldState.error} />
        </div>
      </div>
    );
  }
  return CreateFieldComponent;
}

export default CreateFieldComponentWrapper;

export const InputField = CreateFieldComponentWrapper(Input);
export const TextAreaField = CreateFieldComponentWrapper(TextArea);
